import React, { useState, useRef, useEffect } from 'react';
import { Box, Badge } from '@mantine/core';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

import Landing from '@/components/Landing';
import About from '@/components/About';
import Onboarding from '@/components/Onboarding';
import PostAnalysis from '@/components/PostAnalysis';
import AudienceFeedback from '@/components/AudienceFeedback';
import PostImprovements from '@/components/PostImprovements';
import { StepType, AnalysisData, Improvement, Attachment, FormData, PollOption } from '@/types';
import { generatePersonas, processAttachmentsOnce } from '@/api/analysis';
import logger from '@/utils/logger';
import './global.css'; // put your styles here

// Extend Window interface to include our custom property
declare global {
  interface Window {
    __preloadedPersonas?: Promise<any>;
  }
}

// Extend FormData interface to include processed media content
interface ExtendedFormData extends FormData {
  _processedMediaContent?: string;
}

function App() {
  const [formData, setFormData] = useState<ExtendedFormData>({
    linkedinUrl: '',
    userIndustry: '',
    userRole: '',
    userExperience: '',
    purpose: '',
    tone: '',
    targetAudience: '',
    postContent: '',
    postType: 'text',
    files: [],
    attachments: [],
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Set page title based on current route
  useEffect(() => {
    const pageTitles: Record<string, string> = {
      '/': 'LinkedIn Post Tester - Home',
      '/onboarding': 'LinkedIn Post Tester - Onboarding',
      '/create-post': 'LinkedIn Post Tester - Create Your Post',
      '/analysis': 'LinkedIn Post Tester - Analyzing Your Post',
      '/results': 'LinkedIn Post Tester - Audience Feedback',
      '/recommendations': 'LinkedIn Post Tester - Recommendations'
    };
    
    document.title = pageTitles[location.pathname] || 'LinkedIn Post Tester';
    
    // Also update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      const descriptions: Record<string, string> = {
        '/': 'Test your LinkedIn posts with AI-powered audience feedback before posting.',
        '/onboarding': 'Tell us about yourself and your target audience for accurate LinkedIn post feedback.',
        '/create-post': 'Create your LinkedIn post and see how your audience will react.',
        '/analysis': 'Analyzing your LinkedIn post to provide accurate audience feedback.',
        '/results': 'See how your audience will react to your LinkedIn post.',
        '/recommendations': 'Get AI-powered improvements for your LinkedIn post.'
      };
      
      metaDescription.setAttribute('content', descriptions[location.pathname] || 'Test your LinkedIn posts with AI-powered audience feedback.');
    }
  }, [location.pathname]);
  
  // Process files into attachments whenever files array changes or poll information changes
  useEffect(() => {
    // Handle files
    if (formData.files.length > 0) {
      // Create attachments from files
      const attachments: Attachment[] = formData.files.map(file => {
        let type: 'image' | 'video' | 'document' = 'document';
        
        if (file.type.startsWith('image/')) {
          type = 'image';
        } else if (file.type.startsWith('video/')) {
          type = 'video';
        }
        
        return { type, file };
      });
      
      console.log(`Converting ${attachments.length} files to attachments:`, 
        attachments.map(a => `${a.type}: ${a.file?.name} (${a.file?.type})`));
      
      // Update form with attachments
      setFormData(prev => ({ ...prev, attachments }));
      
      // Process attachments immediately (especially videos) to avoid multiple processing
      const processMedia = async () => {
        try {
          const updatedFormData = { ...formData, attachments };
          logger.info('Processing media attachments immediately after upload');
          const mediaContent = await processAttachmentsOnce(updatedFormData);
          
          if (mediaContent) {
            logger.info('Successfully processed media content on upload');
            setFormData(prev => ({ 
              ...prev, 
              _processedMediaContent: mediaContent 
            }));
          }
        } catch (error) {
          logger.error('Error processing media attachments on upload:', error);
        }
      };
      
      // Only process if we have media files
      const hasMediaFiles = attachments.some(a => a.type === 'image' || a.type === 'video');
      if (hasMediaFiles) {
        processMedia();
      }
    } 
    // Handle poll type
    else if (formData.postType === 'poll' && formData.pollOptions && formData.pollOptions.length > 0) {
      // Create poll attachment
      const pollAttachment: Attachment = {
        type: 'poll',
        pollOptions: formData.pollOptions,
        pollDuration: formData.pollDuration
      };
      
      console.log('Creating poll attachment:', pollAttachment);
      
      // Update form with poll attachment
      setFormData(prev => ({ 
        ...prev, 
        attachments: [pollAttachment] 
      }));
    }
    // Clear attachments if no files or poll
    else if ((formData.attachments?.length || 0) > 0 && formData.postType !== 'poll') {
      // Clear attachments and processed content if files are empty and not a poll
      setFormData(prev => ({ 
        ...prev, 
        attachments: [],
        _processedMediaContent: undefined
      }));
    }
  }, [formData.files, formData.postType, formData.pollOptions, formData.pollDuration]);
  
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [improvements, setImprovements] = useState<Improvement[]>([]);
  const [inlineChats, setInlineChats] = useState<Record<string, boolean>>({});
  
  // Ref to store preloaded personas
  const preloadedPersonasRef = useRef<Promise<any> | null>(null);
  
  // Effect to preload personas when navigating from onboarding to analysis
  useEffect(() => {
    // When transitioning from onboarding to analysis path
    if (location.pathname === '/create-post' && location.state?.navigatingToAnalysis) {
      try {
        logger.info('Preloading personas as user is about to move to analysis');
        
        if (!window.__preloadedPersonas) {
          logger.info('Starting persona generation in preparation for analysis');
          window.__preloadedPersonas = generatePersonas(formData);
        }
      } catch (error) {
        logger.error('Failed to preload personas:', error);
      }
    }
    
    // When actually moving to analysis path
    if (location.pathname === '/analysis') {
      try {
        logger.info('Checking for preloaded personas from earlier steps');
        
        if (window.__preloadedPersonas) {
          logger.info('Found preloaded personas from earlier step - using them for analysis');
          preloadedPersonasRef.current = window.__preloadedPersonas;
          console.log('Using preloaded personas from earlier step');
        } else {
          // Fallback - start persona generation now if not already started
          logger.info('No preloaded personas found - starting generation now');
          preloadedPersonasRef.current = generatePersonas(formData);
        }
      } catch (error) {
        logger.error('Failed to handle preloaded personas:', error);
        preloadedPersonasRef.current = null;
      }
    }
  }, [location.pathname, location.state, formData]);
  
  const handleFormChange = (data: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };
  
  const resetApplication = () => {
    // Reset application state to create a new test
    setFormData({
      linkedinUrl: '',
      userIndustry: '',
      userRole: '',
      userExperience: '',
      purpose: '',
      tone: '',
      targetAudience: '',
      postContent: '',
      postType: 'text',
      files: [],
      attachments: [],
      _processedMediaContent: undefined,
    });
    setAnalysisData(null);
    setImprovements([]);
    setInlineChats({});
    // Clear any preloaded personas
    preloadedPersonasRef.current = null;
    window.__preloadedPersonas = undefined;
    // Navigate back to home
    navigate('/');
  };
  
  const toggleInlineChat = (personaId: string) => {
    setInlineChats(prev => ({
      ...prev,
      [personaId]: !prev[personaId]
    }));
  };
  
  const handleImprovementsGenerated = (newImprovements: Improvement[]) => {
    setImprovements(newImprovements);
    navigate('/recommendations');
  };

  const handleCreateImprovedPost = (improvedContent: string) => {
    // Create a new post with the improved content but keep other settings
    setFormData(prev => ({
      ...prev,
      postContent: improvedContent
    }));
    // Store a flag to indicate we're returning with an improved post
    sessionStorage.setItem('returnToPostCreation', 'true');
    // Go back to create post step
    navigate('/create-post');
  };

  // Navigate from landing to onboarding
  const handleLandingComplete = () => {
    navigate('/onboarding');
  };

  // Navigate from onboarding to analysis
  const handleOnboardingComplete = () => {
    // Mark that we're about to navigate to analysis so we can preload personas
    navigate('/analysis', { state: { navigatingToAnalysis: true } });
  };

  // Handle when analysis is complete
  const handleAnalysisComplete = (data: AnalysisData) => {
    setAnalysisData(data);
    navigate('/results');
  };

  return (
    <Box>
      {import.meta.env.DEV && (
        <Badge 
          color="orange" 
          variant="filled" 
          style={{ 
            position: 'fixed', 
            bottom: '1rem', 
            right: '1rem', 
            zIndex: 9999 
          }}
        >
          DEV MODE
        </Badge>
      )}
      
      <Routes>
        <Route path="/" element={
          <Box className="home-page">
          <Landing 
            formData={formData} 
            onFormChange={handleFormChange} 
            onComplete={handleLandingComplete} 
          />
          </Box>
        } />
        
        <Route path="/about" element={<About />} />
        
        <Route path="/onboarding" element={
          <Onboarding 
            formData={formData} 
            onFormChange={handleFormChange} 
            onComplete={handleOnboardingComplete} 
          />
        } />
        
        <Route path="/create-post" element={
          <Onboarding 
            formData={formData} 
            onFormChange={handleFormChange} 
            onComplete={handleOnboardingComplete} 
          />
        } />
        
        <Route path="/analysis" element={
          <PostAnalysis 
            formData={formData}
            preloadedPersonas={preloadedPersonasRef.current}
            onAnalysisComplete={handleAnalysisComplete} 
          />
        } />
        
        <Route path="/results" element={
          analysisData ? (
            <AudienceFeedback 
              analysisData={analysisData}
              formData={formData}
              inlineChats={inlineChats}
              onToggleInlineChat={toggleInlineChat}
              onViewImprovements={handleImprovementsGenerated}
              onCreateNewTest={resetApplication}
            />
          ) : (
            <Navigate to="/onboarding" />
          )
        } />
        
        <Route path="/recommendations" element={
          analysisData ? (
            <PostImprovements 
              analysisData={{
                ...analysisData,
                improvements: improvements
              }}
              formData={formData}
              onBackToFeedback={() => navigate('/results')}
              onCreateNewTest={resetApplication}
              onCreateImprovedPost={handleCreateImprovedPost}
            />
          ) : (
            <Navigate to="/onboarding" />
          )
        } />
        
        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Box>
  );
}

export default App; 